import { useState } from 'react';

interface PerfexSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  perfexUrl?: string;
  perfexToken?: string;
  onSave: (url: string, token: string) => void;
}

export function PerfexSyncModal({ isOpen, onClose, perfexUrl = '', perfexToken = '', onSave }: PerfexSyncModalProps) {
  const [url, setUrl] = useState(perfexUrl);
  const [token, setToken] = useState(perfexToken);
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave(url, token);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setUrl(perfexUrl);
    setToken(perfexToken);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md border border-gray-200 dark:border-gray-700 my-8" role="dialog" aria-labelledby="perfex-sync-title">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 id="perfex-sync-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                Configurar Perfex CRM
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Configure a integração com Perfex CRM
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Fechar"
            >
              <i className="fas fa-times text-lg" aria-hidden />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="perfexUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                URL do Perfex CRM *
              </label>
              <input
                id="perfexUrl"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://seu-perfex.com"
                required
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                URL completa da sua instância Perfex CRM
              </p>
            </div>

            <div>
              <label htmlFor="perfexToken" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Token de API *
              </label>
              <input
                id="perfexToken"
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="••••••••••••••••"
                required
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Token de API do Perfex CRM (encontrado em Configurações &gt; API)
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-4">
              <h3 className="font-medium text-gray-800 dark:text-gray-200 text-sm mb-2">Como obter o token</h3>
              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <li>• Acesse seu Perfex CRM</li>
                <li>• Vá em Configurações &gt; API</li>
                <li>• Gere ou copie seu token de API</li>
                <li>• Cole o token no campo acima</li>
              </ul>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving || !url || !token}
                className="flex-1 px-4 py-2.5 text-sm font-medium bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Salvando...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Salvar Configuração
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
